import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Calculator, Lock, Mail, ArrowRight, RotateCcw, CheckCircle2 } from "lucide-react";
import { API } from "../../App";
import { GOLD, GOLD_DARK, NAVY, Reveal, SectionHead, Tag } from "./ui";
import { CALCULATORS, ENERGI } from "../../data/business/calculators";

const toNumber = (v) => {
  // Dansk talnotation: punktum er tusindseparator, komma er decimalkomma.
  // Uden det blev "10.000" km læst som 10 km, og brændstoffet blev regnet forkert.
  let s = String(v ?? "").replace(/[\s\u00a0]/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const INPUT =
  "w-full px-3 py-2.5 rounded-lg border bg-white focus:outline-none text-[15px] text-slate-900";
const INPUT_OK = "border-slate-200 focus:border-slate-400";
const INPUT_ERR = "border-red-500 focus:border-red-500";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const FieldError = ({ id, msg }) =>
  msg ? (
    <span id={id} role="alert" className="block text-xs font-medium text-red-600 mt-1.5" data-testid={id}>
      {msg}
    </span>
  ) : null;

const Choice = ({ f, value, options, onChange }) => (
  <div className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</span>
    <div className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1" role="group"
         data-testid={`calc-choice-${f.key}`}>
      {options.map((o) => {
        const aktiv = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(f.key, o.value)}
            aria-pressed={aktiv}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              aktiv ? "text-white" : "text-slate-600 hover:text-slate-900"
            }`}
            style={aktiv ? { backgroundColor: NAVY } : undefined}
            data-testid={`calc-choice-${f.key}-${o.value}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const Field = ({ f, value, error, onChange }) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</span>
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={value ?? ""}
        placeholder={f.placeholder}
        onChange={(e) => onChange(f.key, e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `calc-error-${f.key}` : undefined}
        className={`${INPUT} ${error ? INPUT_ERR : INPUT_OK} pr-24`}
        data-testid={`calc-input-${f.key}`}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">{f.unit}</span>
    </div>
    <FieldError id={`calc-error-${f.key}`} msg={error} />
    {f.help && !error && <span className="block text-xs text-slate-500 mt-1">{f.help}</span>}
  </label>
);

const STARTVAERDIER = (calc) => {
  const v = {};
  calc.groups.forEach((g) => g.fields.forEach((f) => { if (f.default) v[f.key] = f.default; }));
  return v;
};

const CalculatorPanel = ({ calc }) => {
  const [values, setValues] = useState(() => STARTVAERDIER(calc));
  const [errors, setErrors] = useState({});
  const [leadErrors, setLeadErrors] = useState({});
  const [gateOpen, setGateOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", company: "" });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const drivkraft = values.drivkraft || "diesel";
  const maaleenhed = values.consumption_unit || "pr_100";
  // Forbrugsfeltets etiket, enhed og pladsholder følger drivkraften og måleenheden.
  const opslag = (f) => {
    if (f.energi === "forbrug") return { ...f, ...ENERGI[drivkraft][maaleenhed] };
    if (f.energi === "pris") return { ...f, ...ENERGI[drivkraft].pris };
    return f;
  };

  const set = (key, v) => {
    setValues((p) => {
      const next = { ...p, [key]: v };
      // Et forbrugstal betyder noget andet for el end for diesel, så det ryddes.
      if (key === "drivkraft" && p.drivkraft !== v) { next.consumption = ""; next.fuel_price = ""; }
      return next;
    });
    if (errors[key]) setErrors((p) => ({ ...p, [key]: undefined }));
  };
  const setLeadField = (key, v) => {
    setLead((p) => ({ ...p, [key]: v }));
    if (leadErrors[key]) setLeadErrors((p) => ({ ...p, [key]: undefined }));
  };

  const openGate = () => {
    const missing = (calc.required || []).filter((k) => toNumber(values[k]) <= 0);
    if (missing.length) {
      const next = {};
      missing.forEach((k) => { next[k] = "Udfyld feltet med et tal større end 0, beregningen kan ikke laves uden."; });
      setErrors(next);
      document.querySelector(`[data-testid="calc-input-${missing[0]}"]`)?.focus();
      return;
    }
    setErrors({});
    setGateOpen(true);
    setTimeout(() => {
      document.getElementById(`gate-${calc.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = {};
    if (!lead.name.trim()) found.name = "Skriv dit navn.";
    if (!lead.email.trim()) found.email = "Skriv din e-mail, beregningen sendes dertil.";
    else if (!EMAIL_RE.test(lead.email.trim())) found.email = "E-mailen ser ikke rigtig ud. Tjek at der er et @ og et domæne.";
    setLeadErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.querySelector(`[data-testid="calc-${first}-${calc.id}"]`)?.focus();
      return;
    }
    setSubmitting(true);
    const inputs = {};
    calc.groups.forEach((g) => g.fields.forEach((f) => {
      inputs[f.key] = f.type === "choice" ? (values[f.key] || f.default) : toNumber(values[f.key]);
    }));
    try {
      const res = await axios.post(`${API}/business/calculator`, {
        type: calc.id,
        inputs,
        name: lead.name.trim(),
        email: lead.email.trim(),
        phone: lead.phone.trim(),
        company: lead.company.trim(),
      });
      setResult(res.data);
      setGateOpen(false);
      toast.success("Beregningen er sendt til din mail");
      setTimeout(() => {
        document.getElementById(`result-${calc.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lave beregningen. Prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setValues(STARTVAERDIER(calc));
    setGateOpen(false);
    setErrors({});
    setLeadErrors({});
  };

  return (
    <div data-testid={`calculator-${calc.id}`}>
      <div className="max-w-3xl mb-10">
        <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-4">{calc.title}</h2>
        <p className="text-slate-600 leading-relaxed">{calc.lead}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Felter */}
        <div className="lg:col-span-7 space-y-9">
          {calc.groups.map((g) => (
            <div key={g.title}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-5" style={{ color: GOLD_DARK }}>{g.title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {g.fields.map((f) => {
                  if (f.type === "choice") {
                    return (
                      <Choice
                        key={f.key}
                        f={f}
                        value={values[f.key] || f.default}
                        options={f.optionsFrom ? ENERGI[drivkraft][f.optionsFrom] : f.options}
                        onChange={set}
                      />
                    );
                  }
                  const felt = opslag(f);
                  return <Field key={f.key} f={felt} value={values[f.key]} error={errors[f.key]} onChange={set} />;
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Resultat / gate */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-7">
            {!result && !gateOpen && (
              <div data-testid={`calc-gate-closed-${calc.id}`}>
                <Calculator className="h-7 w-7 mb-4" style={{ color: GOLD_DARK }} strokeWidth={1.6} />
                <h3 className="font-display text-xl font-medium text-slate-900 mb-3">Klar til at se resultatet?</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Vi regner på de tal, du har indtastet, og sender beregningen til din mail, så har du den sort på hvidt,
                  når du skal bruge den over for bank, leasingselskab eller kunde.
                </p>
                <button onClick={openGate}
                  className="group w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-shadow hover:shadow-lg"
                  style={{ backgroundColor: GOLD, color: NAVY }} data-testid={`calc-show-result-${calc.id}`}>
                  Se resultatet <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <p className="flex items-start gap-2 text-xs text-slate-500 mt-4">
                  <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  Vi bruger kun dine oplysninger til at sende beregningen og til at kontakte dig om den. Ingen videresalg.
                </p>
              </div>
            )}

            {gateOpen && !result && (
              <form id={`gate-${calc.id}`} onSubmit={submit} noValidate className="space-y-4" data-testid={`calc-gate-form-${calc.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" style={{ color: GOLD_DARK }} aria-hidden />
                  <h3 className="font-display text-lg font-medium text-slate-900">Hvor skal beregningen sendes?</h3>
                </div>
                <div>
                  <input type="text" value={lead.name} onChange={(e) => setLeadField("name", e.target.value)}
                    placeholder="Navn *" aria-label="Navn"
                    aria-invalid={leadErrors.name ? true : undefined}
                    aria-describedby={leadErrors.name ? `calc-error-name-${calc.id}` : undefined}
                    className={`${INPUT} ${leadErrors.name ? INPUT_ERR : INPUT_OK}`} data-testid={`calc-name-${calc.id}`} />
                  <FieldError id={`calc-error-name-${calc.id}`} msg={leadErrors.name} />
                </div>
                <div>
                  <input type="email" value={lead.email} onChange={(e) => setLeadField("email", e.target.value)}
                    placeholder="Email *" aria-label="E-mail"
                    aria-invalid={leadErrors.email ? true : undefined}
                    aria-describedby={leadErrors.email ? `calc-error-email-${calc.id}` : undefined}
                    className={`${INPUT} ${leadErrors.email ? INPUT_ERR : INPUT_OK}`} data-testid={`calc-email-${calc.id}`} />
                  <FieldError id={`calc-error-email-${calc.id}`} msg={leadErrors.email} />
                </div>
                <input type="tel" value={lead.phone} onChange={(e) => setLeadField("phone", e.target.value)}
                  placeholder="Telefon" aria-label="Telefon" className={`${INPUT} ${INPUT_OK}`} data-testid={`calc-phone-${calc.id}`} />
                <input type="text" value={lead.company} onChange={(e) => setLeadField("company", e.target.value)}
                  placeholder="Virksomhed" aria-label="Virksomhed" className={`${INPUT} ${INPUT_OK}`} data-testid={`calc-company-${calc.id}`} />
                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ backgroundColor: GOLD, color: NAVY }} data-testid={`calc-submit-${calc.id}`}>
                  {submitting ? "Beregner..." : "Send mig beregningen"}
                </button>
                <button type="button" onClick={() => setGateOpen(false)} className="w-full text-sm text-slate-500 hover:text-slate-700">
                  Tilbage
                </button>
              </form>
            )}

            {result && (
              <div id={`result-${calc.id}`} data-testid={`calc-result-${calc.id}`}>
                <div className="flex items-center gap-2 mb-5">
                  <CheckCircle2 className="h-4 w-4" style={{ color: GOLD_DARK }} />
                  <Tag>{result.label}</Tag>
                </div>
                <p className="font-display text-2xl md:text-3xl font-medium text-slate-900 leading-tight mb-6">
                  {result.headline}
                </p>
                <dl className="divide-y divide-slate-200 border-t border-slate-200">
                  {result.rows.map(([k, v], i) => (
                    <div key={i} className="flex items-baseline justify-between gap-4 py-2.5" data-testid={`calc-row-${i}`}>
                      <dt className="text-sm text-slate-600">{k}</dt>
                      <dd className="text-sm font-semibold text-slate-900 whitespace-nowrap">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-slate-500 leading-relaxed mt-5">{result.note}</p>
                <p className="text-xs text-slate-500 mt-3">Beregningen er også sendt til {lead.email}.</p>
                <Link to="/business/kontakt"
                  className="mt-6 w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2"
                  style={{ backgroundColor: GOLD, color: NAVY }} data-testid={`calc-cta-${calc.id}`}>
                  Få tallene gennemgået <ArrowRight className="h-4 w-4" />
                </Link>
                <button onClick={reset} className="mt-3 w-full text-sm text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1.5">
                  <RotateCcw className="h-3.5 w-3.5" /> Lav en ny beregning
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Alle tre beregnere med faneblade. Bruges som blok-type "calculators". */
export const CalculatorsBlock = ({ heading, intro }) => {
  const [active, setActive] = useState(CALCULATORS[0].id);
  const calc = CALCULATORS.find((c) => c.id === active);
  return (
    <section className="py-20 md:py-28 bg-white relative" data-testid="block-calculators">
      <div className="max-w-[1680px] mx-auto px-4 md:px-8 lg:px-12">
        <SectionHead overline="Beregnere" title={heading || "Regn på din egen forretning"} intro={intro} className="mb-10" />
        <div className="flex flex-wrap gap-2 mb-12 border-b border-slate-200 pb-5">
          {CALCULATORS.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)}
              aria-pressed={active === c.id}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                active === c.id ? "text-white" : "text-slate-600 hover:bg-slate-100 border border-slate-200"
              }`}
              style={active === c.id ? { backgroundColor: NAVY } : undefined}
              data-testid={`calc-tab-${c.id}`}>
              {c.nav}
            </button>
          ))}
        </div>
        <Reveal key={active}>
          <CalculatorPanel calc={calc} />
        </Reveal>
      </div>
    </section>
  );
};

export default CalculatorsBlock;
